import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { DataGrid } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import CustomDataGridToolBar from "components/shared/CustomDataGridToolBar";
import { Edit } from "@mui/icons-material";
import { IconButton, Tooltip, Box, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { esES } from "@mui/x-data-grid/locales";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CustomToastContent = ({ title, subtitle }) => (
  <div>
    <strong style={{ fontSize: "16px" }}>{title}</strong>
    {subtitle && <p style={{ fontSize: "14px" }}>{subtitle}</p>}
  </div>
);
CustomToastContent.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};
CustomToastContent.defaultProps = { title: "", subtitle: "" };

function TestimoniosList() {
  const api_key = localStorage.getItem("access_token") || "";

  const msgOk = (title, subtitle = "") =>
    toast.success(<CustomToastContent title={title} subtitle={subtitle} />, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
      transition: Bounce,
    });

  const msgError = (title, subtitle = "") =>
    toast.error(<CustomToastContent title={title} subtitle={subtitle} />, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
      transition: Bounce,
    });

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [""],
  });

  const columnVisibilityModel = { id: false };

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  const fetchTestimonios = () => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/testimonios?lang=1`, {
      method: "GET",
      headers: { authToken: api_key },
    })
      .then((res) => res.json())
      .then((data) => {
        const mapped = (Array.isArray(data) ? data : [])
          .sort((a, b) => a.POS - b.POS)
          .map((item) => ({
            id: item.ID,
            key: item.ID,
            nombre: item.NOMBRE,
            texto: item.TEXTO,
            pos: item.POS,
            active: Boolean(item.ACTIVO),
          }));
        setRows(mapped);
      })
      .catch((err) => console.error("Error fetching testimonios:", err));
  };

  const columns = [
    {
      field: "id",
      headerName: "",
      width: 30,
    },
    {
      field: "nombre",
      type: "string",
      headerName: "NOMBRE",
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 180,
      sortable: true,
    },
    {
      field: "texto",
      type: "string",
      headerName: "TESTIMONIO",
      flex: 2,
      headerAlign: "center",
      align: "left",
      minWidth: 250,
      sortable: false,
      /* Muestra los primeros 120 caracteres para no desbordar la celda */
      renderCell: (params) => (
        <span style={{ whiteSpace: "normal", fontSize: "12px" }}>
          {params.value?.substring(0, 120)}
          {params.value?.length > 120 ? "…" : ""}
        </span>
      ),
    },
    {
      field: "pos",
      type: "number",
      headerName: "POS.",
      width: 80,
      headerAlign: "center",
      align: "center",
      sortable: true,
    },
    {
      field: "active",
      headerName: "ACTIVO",
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <Chip label="ACTIVO" color="success" variant="outlined" />
        ) : (
          <Chip label="INACTIVO" color="error" variant="outlined" />
        ),
    },
    {
      field: "key",
      headerName: "ACCIONES",
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar Testimonio">
            <IconButton>
              <Link to={`/admin/testimonios/${params.value}`}>
                <Edit />
              </Link>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  /* Carga los testimonios al montar (idioma por defecto 1 para el listado CRM) */
  useEffect(() => {
    fetchTestimonios();
  }, []);

  /** Pone todos los testimonios activos=0 (oculta la sección en toda la web). */
  const handleHideAll = () => {
    setVisibilityLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/testimonios/visibility`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        authToken: api_key,
      },
      body: JSON.stringify({ active: false }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || "Error");
        return res.json();
      })
      .then(() => {
        msgOk("Sección de testimonios ocultada en toda la web.");
        fetchTestimonios();
      })
      .catch((err) => {
        msgError("Error al ocultar testimonios", err?.message || "");
      })
      .finally(() => setVisibilityLoading(false));
  };

  /** Pone todos los testimonios activos=1 (muestra la sección en toda la web). */
  const handleShowAll = () => {
    setVisibilityLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/testimonios/visibility`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        authToken: api_key,
      },
      body: JSON.stringify({ active: true }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || "Error");
        return res.json();
      })
      .then(() => {
        msgOk("Sección de testimonios visible en toda la web.");
        fetchTestimonios();
      })
      .catch((err) => {
        msgError("Error al mostrar testimonios", err?.message || "");
      })
      .finally(() => setVisibilityLoading(false));
  };

  useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  useEffect(() => {
    setFilteredRows(
      rows.filter((row) =>
        Object.values(row).some((val) =>
          val?.toString().toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    );
  }, [searchValue]);

  const onSearchValueChange = (event) => setSearchValue(event.target.value);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="h6" color="white">
                      Testimonios ({filteredRows.length})
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Tooltip title="Oculta la sección de testimonios en toda la web (todos pasan a inactivos)">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityOffIcon />}
                          onClick={handleHideAll}
                          disabled={visibilityLoading || rows.every((r) => !r.active)}
                          sx={{ color: "white", borderColor: "rgba(255,255,255,0.7)", mr: 1 }}
                        >
                          Ocultar en la web
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Muestra la sección de testimonios en toda la web (todos pasan a activos)">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={handleShowAll}
                          disabled={
                            visibilityLoading || (rows.length > 0 && rows.every((r) => r.active))
                          }
                          sx={{ color: "white", borderColor: "rgba(255,255,255,0.7)", mr: 1 }}
                        >
                          Mostrar en la web
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Añadir testimonio">
                      <a
                        href="/admin/testimonios/0"
                        title="Añadir testimonio"
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <IconButton color="inherit" size="large" component="span">
                          <AddIcon />
                        </IconButton>
                      </a>
                    </Tooltip>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  initialState={{
                    pagination: { paginationModel: { page: 0, pageSize: 15 } },
                  }}
                  slots={{ toolbar: CustomDataGridToolBar }}
                  slotProps={{ toolbar: { searchValue, onSearchValueChange } }}
                  filterModel={filterModel}
                  onFilterModelChange={(m) => setFilterModel(m)}
                  columnVisibilityModel={columnVisibilityModel}
                  sx={{ height: "Calc(100vh - 300px)", minHeight: 400 }}
                  pageSizeOptions={[5, 10, 15]}
                  checkboxSelection={false}
                  disableDensitySelector
                  disableColumnMenu
                  disableColumnSelector
                  disableRowSelectionOnClick
                  localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <ToastContainer />
    </DashboardLayout>
  );
}

export default TestimoniosList;
